import { useState, useEffect, useCallback } from "react";
import { API, useAuth } from "@/App";
import axios from "axios";
import MarketingShell from "@/components/MarketingShell";
import {
  Mail, Eye, MousePointer, Share2,
  Sparkles, Loader2,
} from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

export default function MarketingAnalytics() {
  const { token } = useAuth();
  const [dateRange, setDateRange] = useState("30d");
  const [aiAnalysis, setAiAnalysis] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [emailTrend, setEmailTrend] = useState([]);
  const [socialEngagement, setSocialEngagement] = useState([]);
  const [campaignPerformance, setCampaignPerformance] = useState([]);
  const [kpis, setKpis] = useState({ total_sent: 0, avg_open_rate: 0, avg_click_rate: 0, total_reach: 0 });
  const [loading, setLoading] = useState(true);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/marketing/analytics/summary`, {
        params: { range: dateRange },
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = res.data || {};
      setKpis(d.kpis || {});
      setEmailTrend(d.email_trend || []);
      setSocialEngagement(d.social_engagement || []);
      // Normalize snake_case server fields → camelCase the existing rows expect.
      setCampaignPerformance((d.campaign_performance || []).map((c) => ({
        id: c.id,
        name: c.name,
        sentDate: c.sent_date,
        audience: c.audience,
        delivered: c.delivered,
        deliveredRate: c.delivered_rate,
        opened: c.opened,
        openRate: c.open_rate,
        clicked: c.clicked,
        clickRate: c.click_rate,
        unsubs: c.unsubs,
        status: c.status,
      })));
    } catch {
      setEmailTrend([]);
      setSocialEngagement([]);
      setCampaignPerformance([]);
      setKpis({ total_sent: 0, avg_open_rate: 0, avg_click_rate: 0, total_reach: 0 });
    } finally {
      setLoading(false);
    }
  }, [token, dateRange]);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  // Aliases preserved for the legacy chart references below.
  const EMAIL_TREND = emailTrend;
  const SOCIAL_ENGAGEMENT = socialEngagement;
  const CAMPAIGN_PERFORMANCE = campaignPerformance;

  const totalSent = kpis.total_sent || 0;
  const avgOpenRate = (kpis.avg_open_rate || 0).toFixed(1);
  const avgClickRate = (kpis.avg_click_rate || 0).toFixed(1);
  const totalReach = SOCIAL_ENGAGEMENT.reduce((s, c) => s + c.likes + c.comments + c.shares, 0);

  const analyzeWithClaude = async () => {
    setAiLoading(true);
    try {
      const res = await axios.post(`${API}/ai/dashboard-briefing`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setAiAnalysis(res.data?.briefing || getFallbackAnalysis());
    } catch {
      setAiAnalysis(getFallbackAnalysis());
    } finally {
      setAiLoading(false);
    }
  };

  const getFallbackAnalysis = () =>
    `Connect a channel and send your first campaign to unlock AI-driven performance insights here.`;

  return (
    <MarketingShell
      title="Analytics"
      subtitle="Campaign performance and audience insights"
      actions={
        <select value={dateRange} onChange={e => setDateRange(e.target.value)} className="input" style={{ fontSize: 11, height: 32, width: 150 }} data-testid="date-range">
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
          <option value="90d">Last 90 days</option>
          <option value="custom">Custom range</option>
        </select>
      }
    >
    <div data-testid="marketing-analytics">
      {/* KPI Row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 22 }}>
        {[
          { label: "Total emails sent", value: totalSent.toLocaleString(), icon: Mail },
          { label: "Avg open rate", value: `${avgOpenRate}%`, sub: "Industry avg: 21%", icon: Eye },
          { label: "Avg click rate", value: `${avgClickRate}%`, icon: MousePointer },
          { label: "Total social reach", value: totalReach.toLocaleString(), icon: Share2 },
        ].map(kpi => (
          <div key={kpi.label} className="mkt-kpi" data-testid={`analytics-kpi-${kpi.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="mkt-kpi-icon"><kpi.icon style={{ width: 14, height: 14 }} /></div>
            <div className="mkt-kpi-value">{kpi.value}</div>
            <div className="mkt-kpi-label">{kpi.label}</div>
            {kpi.sub && <div style={{ fontSize: 10, color: "var(--text-tertiary)" }}>{kpi.sub}</div>}
          </div>
        ))}
      </div>

      {/* Charts */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 22 }}>
        <div className="mkt-card mkt-card-pad">
          <h3 className="card-title" style={{ marginBottom: 14 }}>Email performance over time</h3>
          {EMAIL_TREND.length === 0 ? (
            <div data-testid="email-trend-empty" style={{ padding: "40px 10px", textAlign: "center", color: "var(--text-tertiary)", fontSize: 12.5 }}>
              No email sends yet.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={EMAIL_TREND}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: "0.5px solid var(--border)" }} />
                <Area type="monotone" dataKey="sent" stroke="var(--blue)" fill="var(--blue-soft)" strokeWidth={2} />
                <Area type="monotone" dataKey="opened" stroke="var(--success)" fill="var(--success-soft)" strokeWidth={2} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
        <div className="mkt-card mkt-card-pad">
          <h3 className="card-title" style={{ marginBottom: 14 }}>Social engagement by channel</h3>
          {SOCIAL_ENGAGEMENT.length === 0 ? (
            <div data-testid="social-engagement-empty" style={{ padding: "40px 10px", textAlign: "center", color: "var(--text-tertiary)", fontSize: 12.5 }}>
              Connect a social channel to track engagement.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={SOCIAL_ENGAGEMENT}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="channel" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: "0.5px solid var(--border)" }} />
                <Bar dataKey="likes" fill="var(--blue)" radius={[3, 3, 0, 0]} />
                <Bar dataKey="comments" fill="var(--success)" radius={[3, 3, 0, 0]} />
                <Bar dataKey="shares" fill="var(--purple)" radius={[3, 3, 0, 0]} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Campaign Performance Table */}
      <div className="mkt-table-wrap" style={{ marginBottom: 22 }}>
        <div style={{ padding: "14px 18px", borderBottom: "0.5px solid var(--border)" }}>
          <h3 className="card-title">Campaign performance</h3>
        </div>
        {CAMPAIGN_PERFORMANCE.length === 0 ? (
          <div data-testid="campaign-perf-empty" style={{ padding: 40, textAlign: "center", color: "var(--text-tertiary)", fontSize: 12.5 }}>
            No campaign performance data yet. Send your first campaign to populate this table.
          </div>
        ) : (
        <table className="mkt-table">
          <thead>
            <tr>
              {["Campaign", "Sent", "Audience", "Delivered", "Opened", "Clicked", "Unsubs", "Status"].map(h => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {CAMPAIGN_PERFORMANCE.map(c => (
              <tr key={c.id}>
                <td style={{ fontSize: 13, fontWeight: 500 }}>{c.name}</td>
                <td style={{ fontSize: 12, color: "var(--text-secondary)" }}>{c.sentDate}</td>
                <td className="num" style={{ fontSize: 12 }}>{c.audience}</td>
                <td className="num" style={{ fontSize: 12 }}>{c.delivered} <span style={{ fontSize: 10, color: "var(--text-tertiary)" }}>({c.deliveredRate}%)</span></td>
                <td>
                  <div className="num" style={{ fontSize: 12, fontWeight: 540 }}>{c.opened} <span style={{ fontSize: 10, color: "var(--success)" }}>({c.openRate}%)</span></div>
                  <div style={{ height: 3, width: 50, background: "var(--subtle)", borderRadius: 2, marginTop: 2 }}>
                    <div style={{ height: "100%", width: `${c.openRate}%`, background: "var(--success)", borderRadius: 2 }} />
                  </div>
                </td>
                <td className="num" style={{ fontSize: 12 }}>{c.clicked} <span style={{ fontSize: 10, color: "var(--text-tertiary)" }}>({c.clickRate}%)</span></td>
                <td className="num" style={{ fontSize: 12, color: "var(--text-secondary)" }}>{c.unsubs}</td>
                <td>
                  <span className={`tag ${c.status === "sent" ? "tag-green" : "tag-blue"}`}>{c.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </div>

      {/* Claude Analysis */}
      <div className="ai-banner" data-testid="claude-analysis">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div className="ai-pill">
            <Sparkles style={{ width: 11, height: 11 }} /> Claude AI Performance Analysis
          </div>
          {!aiAnalysis && (
            <button onClick={analyzeWithClaude} disabled={aiLoading} className="ai-chip" data-testid="analyze-with-claude">
              {aiLoading ? <><Loader2 style={{ width: 12, height: 12, animation: "spin 1s linear infinite" }} /> Analyzing…</> : "Analyze with Claude"}
            </button>
          )}
        </div>
        {aiAnalysis ? (
          <p style={{ fontSize: 12.5, lineHeight: 1.7 }}>{aiAnalysis}</p>
        ) : (
          <p style={{ fontSize: 12.5, opacity: 0.7 }}>Click "Analyze with Claude" to get AI-powered insights on your campaign performance.</p>
        )}
      </div>
    </div>
    </MarketingShell>
  );
}
